/* eslint-disable @next/next/no-img-element -- covers are plain files, not a Next image pipeline */
"use client";

import { useState } from "react";
import { Monogram } from "@/components/ui/Logo";
import { BLOG_CATEGORIES, postBySlug, postImage } from "@/content/blog";
import { cn } from "@/lib/utils";

/**
 * Light falling across the generated cover. One per article category, so a grid of them
 * never looks like a row of identical grey boxes — all in the site's teal-on-graphite
 * register, only the angle and strength change.
 */
const GLOWS = [
  "radial-gradient(92% 72% at 16% 10%, rgba(45,226,208,0.26), transparent 62%)",
  "radial-gradient(86% 76% at 84% 14%, rgba(139,239,229,0.20), transparent 60%)",
  "radial-gradient(104% 82% at 50% 0%, rgba(45,226,208,0.17), transparent 64%)",
  "radial-gradient(82% 72% at 12% 90%, rgba(139,239,229,0.19), transparent 62%)",
  "radial-gradient(96% 78% at 90% 88%, rgba(45,226,208,0.15), transparent 60%)",
];

/**
 * Article cover photo, with the brand mark standing in when the photo cannot be had.
 *
 * `colour` opts out of the card treatment: on a card the cover sits in the page's monochrome
 * register and finds its colour on hover, but as an article's own hero image there is nothing
 * to hover and no reason to hold the colour back.
 */
export function PostCover({
  slug,
  className,
  eager,
  colour,
}: {
  slug: string;
  className?: string;
  eager?: boolean;
  colour?: boolean;
}) {
  const webp = postImage(slug);
  // No cover for this article: go straight to the gradient rather than requesting a 404. With
  // one, the .webp is shown; if it ever fails, the .jpg kept beside it for social previews is
  // tried, and only when that fails too does the gradient take over.
  const [misses, setMisses] = useState(0);
  const src = misses === 0 ? webp : webp?.replace(/\.webp$/, ".jpg");
  const show = !!webp && misses < 2;
  const category = postBySlug(slug)?.category;
  const glow = GLOWS[(category ? BLOG_CATEGORIES.indexOf(category) : 0) % GLOWS.length];

  return (
    <div className={cn("relative overflow-hidden bg-[linear-gradient(140deg,#161617_0%,#2c2c30_100%)]", className)}>
      {show && (
        <img
          src={src}
          alt=""
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          onError={() => setMisses((n) => n + 1)}
          className={cn(
            "absolute inset-0 size-full object-cover transition-[scale,filter] duration-700 ease-[var(--ease-premium)]",
            !colour && "grayscale group-hover:scale-[1.04] group-hover:grayscale-0",
          )}
        />
      )}
      {!show && (
        <>
          <div className="absolute inset-0" style={{ backgroundImage: glow }} />
          <div className="panel-grid opacity-70" />
          <Monogram
            tone="light"
            className="absolute left-1/2 top-1/2 h-auto w-[22%] min-w-[44px] -translate-x-1/2 -translate-y-1/2 opacity-[0.22] transition-[scale,opacity] duration-700 ease-[var(--ease-premium)] group-hover:scale-[1.06] group-hover:opacity-30"
          />
        </>
      )}
    </div>
  );
}
